/**
 * Date Range Selector Component
 *
 * Two date pickers for selecting a range, plus quick-select buttons.
 * Shows entry count for the selected range. Validates date ordering.
 */

import { useState } from "react";
import { format, subDays, subMonths, subYears, isBefore } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useRelationshipEntryStats } from "@/hooks/useRelationshipEntries";

interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onRangeChange: (start: string, end: string) => void;
}

export function DateRangeSelector({
  startDate,
  endDate,
  onRangeChange,
}: DateRangeSelectorProps) {
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const { data: stats, isLoading: statsLoading } = useRelationshipEntryStats(
    startDate,
    endDate
  );

  // Ensure start <= end when setting dates
  const handleStartChange = (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd");
    if (isBefore(new Date(endDate + "T00:00:00"), date)) {
      onRangeChange(formatted, formatted);
    } else {
      onRangeChange(formatted, endDate);
    }
  };

  const handleEndChange = (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd");
    if (isBefore(date, new Date(startDate + "T00:00:00"))) {
      onRangeChange(formatted, formatted);
    } else {
      onRangeChange(startDate, formatted);
    }
  };

  const quickRanges = [
    {
      label: "Last Week",
      fn: () => {
        const end = new Date();
        const start = subDays(end, 7);
        onRangeChange(format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd"));
      },
    },
    {
      label: "Last Month",
      fn: () => {
        const end = new Date();
        const start = subMonths(end, 1);
        onRangeChange(format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd"));
      },
    },
    {
      label: "3 Months",
      fn: () => {
        const end = new Date();
        const start = subMonths(end, 3);
        onRangeChange(format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd"));
      },
    },
    {
      label: "Last Year",
      fn: () => {
        const end = new Date();
        const start = subYears(end, 1);
        onRangeChange(format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd"));
      },
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {quickRanges.map((range) => (
          <Button
            key={range.label}
            variant="outline"
            size="sm"
            onClick={range.fn}
          >
            {range.label}
          </Button>
        ))}
      </div>

      <div className="flex items-center gap-3 text-sm flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="gap-1"
          onClick={() => setShowStartPicker(true)}
        >
          <CalendarDays className="h-3 w-3" />
          {startDate
            ? format(new Date(startDate + "T00:00:00"), "MMM d, yyyy")
            : "Start"}
        </Button>
        <span className="text-muted-foreground">to</span>
        <Button
          variant="outline"
          size="sm"
          className="gap-1"
          onClick={() => setShowEndPicker(true)}
        >
          <CalendarDays className="h-3 w-3" />
          {endDate
            ? format(new Date(endDate + "T00:00:00"), "MMM d, yyyy")
            : "End"}
        </Button>
        <span className="text-muted-foreground text-xs">
          {statsLoading
            ? "..."
            : stats
              ? `${stats.count} entries${stats.average_rating !== null ? ` · avg ${stats.average_rating.toFixed(1)}` : ""}`
              : ""}
        </span>
      </div>

      <Dialog open={showStartPicker} onOpenChange={setShowStartPicker}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>Start Date</DialogTitle>
            <DialogDescription>
              Select the start of the date range.
            </DialogDescription>
          </DialogHeader>
          <Calendar
            mode="single"
            selected={startDate ? new Date(startDate + "T00:00:00") : undefined}
            onSelect={(date) => {
              if (date) {
                handleStartChange(date);
                setShowStartPicker(false);
              }
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showEndPicker} onOpenChange={setShowEndPicker}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>End Date</DialogTitle>
            <DialogDescription>
              Select the end of the date range.
            </DialogDescription>
          </DialogHeader>
          <Calendar
            mode="single"
            selected={endDate ? new Date(endDate + "T00:00:00") : undefined}
            onSelect={(date) => {
              if (date) {
                handleEndChange(date);
                setShowEndPicker(false);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
